<!--
  SPDX-License-Identifier: MIT
  Copyright (c) 2026 Solvetia. Original work by Solvetia — part of the Solvetia
  white-label additions to Chatwoot Community Edition (MIT). Not derived from
  the commercial code under enterprise/.
-->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { applyBrandColor, applyFavicon } from 'dashboard/helper/brandingHelper';
import BrandingAPI from 'dashboard/api/branding';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { buildBrandingPayload } from './helpers';
import { buildBrandingFormData } from './uploadHelpers';

const LOGO_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml';
const FAVICON_ACCEPT = `${LOGO_ACCEPT},image/x-icon`;

export default {
  components: {
    NextButton,
  },
  data() {
    return {
      brandName: '',
      brandColor: '#2781F6',
      logoUrl: '',
      logoDarkUrl: '',
      faviconUrl: '',
      // uploaded asset previews (attachment urls from the account)
      brandLogoUrl: '',
      brandLogoDarkUrl: '',
      brandFaviconUrl: '',
      // pending file selections / removals
      brandLogoFile: null,
      brandLogoDarkFile: null,
      brandFaviconFile: null,
      removeBrandLogo: false,
      removeBrandLogoDark: false,
      removeBrandFavicon: false,
      logoAccept: LOGO_ACCEPT,
      faviconAccept: FAVICON_ACCEPT,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
    }),
    currentAccountId() {
      return Number(this.$route.params.accountId);
    },
  },
  async mounted() {
    // Refetch the account so custom_attributes (branding) is present: the
    // store record can be overwritten by a slim account payload (e.g. from
    // the user profile) that drops custom_attributes. Without this the form
    // would load blank and Save would clear existing branding.
    await this.$store.dispatch('accounts/get');
    this.seedForm();
  },
  methods: {
    seedForm() {
      const attrs =
        this.getAccount(this.currentAccountId)?.custom_attributes || {};
      this.brandName = attrs.brand_name || '';
      this.brandColor = attrs.brand_color || '#2781F6';
      this.logoUrl = attrs.logo_url || '';
      this.logoDarkUrl = attrs.logo_dark_url || '';
      this.faviconUrl = attrs.favicon_url || '';
      this.brandLogoUrl = attrs.brand_logo_url || '';
      this.brandLogoDarkUrl = attrs.brand_logo_dark_url || '';
      this.brandFaviconUrl = attrs.brand_favicon_url || '';
      this.brandLogoFile = null;
      this.brandLogoDarkFile = null;
      this.brandFaviconFile = null;
      this.removeBrandLogo = false;
      this.removeBrandLogoDark = false;
      this.removeBrandFavicon = false;
    },
    previewColor() {
      applyBrandColor(this.brandColor);
    },
    onFileChange(field, event) {
      this[field] = event.target.files[0] || null;
    },
    removeAsset(kind) {
      if (kind === 'logo') {
        this.removeBrandLogo = true;
        this.brandLogoFile = null;
        this.brandLogoUrl = '';
      } else if (kind === 'logoDark') {
        this.removeBrandLogoDark = true;
        this.brandLogoDarkFile = null;
        this.brandLogoDarkUrl = '';
      } else if (kind === 'favicon') {
        this.removeBrandFavicon = true;
        this.brandFaviconFile = null;
        this.brandFaviconUrl = '';
      }
    },
    async save() {
      try {
        await this.$store.dispatch(
          'accounts/update',
          buildBrandingPayload(this.$data)
        );
        const formData = buildBrandingFormData(this.$data);
        if ([...formData.keys()].length) {
          await BrandingAPI.update(formData);
          await this.$store.dispatch('accounts/get');
          this.seedForm();
        }
        const attrs =
          this.getAccount(this.currentAccountId)?.custom_attributes || {};
        applyBrandColor(this.brandColor || null);
        applyFavicon(attrs.brand_favicon_url || this.faviconUrl || null);
        useAlert(this.$t('BRANDING_SETTINGS.SAVED'));
      } catch (e) {
        useAlert(this.$t('BRANDING_SETTINGS.ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6 p-6 max-w-2xl">
    <h2 class="text-xl font-medium text-n-slate-12">
      {{ $t('BRANDING_SETTINGS.TITLE') }}
    </h2>

    <label class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-11">{{
        $t('BRANDING_SETTINGS.BRAND_NAME')
      }}</span>
      <input
        v-model="brandName"
        type="text"
        :placeholder="$t('BRANDING_SETTINGS.BRAND_NAME_PLACEHOLDER')"
      />
    </label>

    <label class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-11">{{
        $t('BRANDING_SETTINGS.BRAND_COLOR')
      }}</span>
      <span class="flex items-center gap-3">
        <input
          v-model="brandColor"
          type="color"
          class="h-9 w-12 p-0"
          @input="previewColor"
        />
        <input
          v-model="brandColor"
          type="text"
          :placeholder="$t('BRANDING_SETTINGS.BRAND_COLOR_PLACEHOLDER')"
          class="!mb-0"
          @input="previewColor"
        />
      </span>
    </label>

    <!-- Uploaded logo (light) -->
    <label class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-11">{{
        $t('BRANDING_SETTINGS.UPLOAD_LOGO')
      }}</span>
      <span class="flex items-center gap-3">
        <img
          v-if="brandLogoUrl"
          :src="brandLogoUrl"
          class="object-contain h-10 rounded bg-n-alpha-2"
        />
        <input
          type="file"
          :accept="logoAccept"
          @change="event => onFileChange('brandLogoFile', event)"
        />
        <NextButton
          v-if="brandLogoUrl"
          slate
          ghost
          xs
          :label="$t('BRANDING_SETTINGS.REMOVE')"
          @click="removeAsset('logo')"
        />
      </span>
      <span class="text-xs text-n-slate-10">{{
        $t('BRANDING_SETTINGS.UPLOAD_HINT')
      }}</span>
    </label>

    <!-- Uploaded logo (dark) -->
    <label class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-11">{{
        $t('BRANDING_SETTINGS.UPLOAD_LOGO_DARK')
      }}</span>
      <span class="flex items-center gap-3">
        <img
          v-if="brandLogoDarkUrl"
          :src="brandLogoDarkUrl"
          class="object-contain h-10 rounded bg-n-solid-3"
        />
        <input
          type="file"
          :accept="logoAccept"
          @change="event => onFileChange('brandLogoDarkFile', event)"
        />
        <NextButton
          v-if="brandLogoDarkUrl"
          slate
          ghost
          xs
          :label="$t('BRANDING_SETTINGS.REMOVE')"
          @click="removeAsset('logoDark')"
        />
      </span>
    </label>

    <!-- Uploaded favicon -->
    <label class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-11">{{
        $t('BRANDING_SETTINGS.UPLOAD_FAVICON')
      }}</span>
      <span class="flex items-center gap-3">
        <img
          v-if="brandFaviconUrl"
          :src="brandFaviconUrl"
          class="object-contain w-6 h-6"
        />
        <input
          type="file"
          :accept="faviconAccept"
          @change="event => onFileChange('brandFaviconFile', event)"
        />
        <NextButton
          v-if="brandFaviconUrl"
          slate
          ghost
          xs
          :label="$t('BRANDING_SETTINGS.REMOVE')"
          @click="removeAsset('favicon')"
        />
      </span>
    </label>

    <!-- Fallback: paste URLs instead of uploading -->
    <label class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-11">{{
        $t('BRANDING_SETTINGS.LOGO_URL')
      }}</span>
      <input
        v-model="logoUrl"
        type="url"
        :placeholder="$t('BRANDING_SETTINGS.LOGO_URL_PLACEHOLDER')"
      />
    </label>

    <label class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-11">{{
        $t('BRANDING_SETTINGS.LOGO_DARK_URL')
      }}</span>
      <input
        v-model="logoDarkUrl"
        type="url"
        :placeholder="$t('BRANDING_SETTINGS.LOGO_DARK_URL_PLACEHOLDER')"
      />
    </label>

    <label class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-11">{{
        $t('BRANDING_SETTINGS.FAVICON_URL')
      }}</span>
      <input
        v-model="faviconUrl"
        type="url"
        :placeholder="$t('BRANDING_SETTINGS.FAVICON_URL_PLACEHOLDER')"
      />
    </label>

    <div>
      <NextButton blue :is-loading="uiFlags.isUpdating" @click="save">
        {{ $t('BRANDING_SETTINGS.SUBMIT') }}
      </NextButton>
    </div>
  </div>
</template>
